import useAuthUser from '@forwardflow/hooks/customHooks/useAuthUser'
import { Spinner } from '@forwardflow/ui'
import { Button } from '@forwardflow/ui-v2'
import { Dropdown, MenuProps } from 'antd'
import { Power01Icon } from '@forwardflow/icons'
import { SelectUserOrganizationModal } from '@forwardflow/modals'
import OrganizationIcon from '@forwardflow/assets/svg/OrganizationIcon'
import { useSelectUserOrganizationModalStore } from '@forwardflow/store/modal/SelectUserOrganizationModal'
import {
   useOnboardingDataLoader,
   useLogout,
} from '@forwardflow/hooks/customHooks/onboarding'
import { SelectOperatorOrganization } from '@forwardflow/components'
import { isOperatorEmail } from '@forwardflow/utils/app.utils'
import { OnboardingDevBar } from '@forwardflow/modules/onboarding/dev'
import { OnboardingShell } from '@forwardflow/modules/onboarding/shell/OnboardingShell'

/**
 * The signed-in setup page: the shared two-column shell with the account
 * menu in the header and the record loader underneath.
 */
export const OnboardingLayout = ({ children }: { children: React.ReactNode }) => {
   const { data: user } = useAuthUser({ revalidateOnMount: true })
   const { openSelectUserOrganizationModal } = useSelectUserOrganizationModalStore()
   const { isLoading, currentOrganization } = useOnboardingDataLoader(user?.email)
   const { logout } = useLogout()

   const items: MenuProps['items'] = [
      {
         label: (
            <button
               className="py-1 font-semibold text-[14px] flex items-center gap-2"
               onClick={logout}
            >
               <Power01Icon className="w-4 h-4" /> Log out
            </button>
         ),
         key: '0',
      },
   ]

   const currentOrganizationObj = user?.userOrganizations?.find(
      (org) => String(org.organizationId) === currentOrganization
   )
   const isOperator = isOperatorEmail(user?.email || '')
   const hasManyOrgs = (user?.userOrganizations?.length || 0) > 1

   const headerRight = (
      <>
         {isOperator && <SelectOperatorOrganization />}
         {!isOperator && hasManyOrgs && (
            <>
               <button
                  type="button"
                  onClick={openSelectUserOrganizationModal}
                  className="h-9 hidden sm:flex items-center bg-white/[0.04] hover:bg-white/[0.08] px-2 rounded-lg text-xs gap-2"
               >
                  <span className="bg-[#27282C] rounded flexc w-6 h-6">
                     <span className="scale-[0.65]">
                        <OrganizationIcon />
                     </span>
                  </span>
                  <span className="max-w-[140px] truncate">
                     {currentOrganizationObj?.organization?.name || 'Organization'}
                  </span>
               </button>
               <SelectUserOrganizationModal />
            </>
         )}
         <Dropdown menu={{ items }} trigger={['click']}>
            <Button
               variant="secondary"
               size="middle"
               className="!w-9 !h-9 !p-0 rounded-full flex items-center justify-center text-sm font-semibold"
               ariaLabel="Account"
            >
               {user?.firstName ? user.firstName[0].toUpperCase() : ''}
            </Button>
         </Dropdown>
      </>
   )

   return (
      <OnboardingShell headerRight={headerRight} above={<OnboardingDevBar />}>
         {isLoading ? (
            <div className="flex items-center justify-center min-h-[320px]">
               <Spinner />
            </div>
         ) : (
            children
         )}
      </OnboardingShell>
   )
}
